/** Builder section 7 — the priced lines for one budget option, as the plan page shows them, and the network's benchmarks. */

import React from 'react';
import { Tabs, tableStyles } from '../../../ui';
import PlanLinesTable from '../PlanLinesTable';
import type { BenchmarkRow, MediaPlanQuote } from '../types';
import { fmtBudget, mutedText } from '../formStyles';
import FormSection from './FormSection';

interface Props {
  quote: MediaPlanQuote | null;
  updating: boolean;
  tierIndex: number;
  onTierChange: (index: number) => void;
}

export const BudgetOptionTabs: React.FC<{ quote: MediaPlanQuote; tierIndex: number; onTierChange: (index: number) => void }> = ({ quote, tierIndex, onTierChange }) =>
  quote.preview.tiers.length > 1 ? (
    <Tabs
      value={String(Math.min(tierIndex, quote.preview.tiers.length - 1))}
      onChange={(v) => onTierChange(Number(v))}
      options={quote.preview.tiers.map((t, i) => ({ value: String(i), label: fmtBudget(t.budgetUsd) }))}
      size="sm"
      ariaLabel="Budget option"
    />
  ) : null;

const Benchmarks: React.FC<{ rows: BenchmarkRow[] }> = ({ rows }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-3)', marginBottom: 8 }}>Network benchmarks</div>
    <div style={tableStyles.wrapScroll}>
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <th style={tableStyles.th}>Ad unit</th>
            <th style={tableStyles.thNum}>CTR</th>
            <th style={tableStyles.thNum}>Video completion</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.unitKey}>
              <td style={tableStyles.td}>
                {b.label}
                {b.note && <div style={{ ...mutedText, fontSize: 11 }}>{b.note}</div>}
              </td>
              <td style={tableStyles.tdNum}>
                {b.ctr || '—'}
                {b.ctrRange && <div style={{ ...mutedText, fontSize: 11 }}>{b.ctrRange}</div>}
              </td>
              <td style={tableStyles.tdNum}>
                {b.vcr || '—'}
                {b.vcrRange && <div style={{ ...mutedText, fontSize: 11 }}>{b.vcrRange}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const PlanLinesSection: React.FC<Props> = ({ quote, updating, tierIndex, onTierChange }) => {
  const tiers = quote?.preview.tiers ?? [];
  const tier = tiers[Math.min(tierIndex, tiers.length - 1)];
  return (
    <FormSection
      id="mp-section-lines"
      title="Plan lines"
      note="The lines the client gets, with the detail behind each rate (that part stays internal)."
      busy={updating && !!tier}
      action={quote ? <BudgetOptionTabs quote={quote} tierIndex={tierIndex} onTierChange={onTierChange} /> : undefined}
    >
      {quote && tier ? <PlanLinesTable tier={tier} request={quote.request} /> : <div style={mutedText}>Lines show here once the plan is priced.</div>}
      {quote && quote.benchmarks.length > 0 && <Benchmarks rows={quote.benchmarks} />}
    </FormSection>
  );
};

export default PlanLinesSection;
